import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, Eye, RotateCcw, Trash2 } from 'lucide-react';
import type { DashboardContainer } from '@ctp/shared';
import { completedAtOf, completionReasonKey } from '../lib/premium';
import { ContainerDetailModal } from './ContainerDetailModal';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Spinner,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from './ui';

/** Neutraler Badge „Abgeschlossen" mit Abschluss-Grund als Tooltip (White-Label, D6). */
export function CompletedBadge({ c }: { c: DashboardContainer }): JSX.Element {
  const { t } = useTranslation();
  return (
    <Badge variant="default" title={t(completionReasonKey(c))} data-testid="completed-badge">
      {t('archive.badge')}
    </Badge>
  );
}

/**
 * Archiv-Reiter „Abgeschlossen" (EPIC Q / ADR-041): EINE Tabelle (Premium + Standard gemischt),
 * sortiert nach Abschlussdatum absteigend (liefert bereits die API), OHNE Polling/Flash (A-122).
 * Details bleiben einsehbar (Klick/Auge); Reaktivieren und Entfernen laufen über den Dashboard-
 * Container. White-Label: keine Quellen-/Anbieter-Nennung.
 */
export function ArchiveTable({
  containers,
  fmtDate,
  token,
  onReactivate,
  onRequestDelete,
  canManage,
  busyId,
}: {
  containers: DashboardContainer[];
  fmtDate: (iso: string | null) => string;
  token?: string | null;
  onReactivate: (c: DashboardContainer) => void;
  onRequestDelete: (c: DashboardContainer) => void;
  /** Rechte wie Löschen: Ersteller ODER Org-/Super-Admin (steuert Reaktivieren + Entfernen). */
  canManage: (c: DashboardContainer) => boolean;
  /** trackedContainerId der laufenden Aktion (Reaktivieren/Löschen) — deaktiviert die Buttons. */
  busyId: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<{ id: string; number: string } | null>(null);

  // Abschluss absteigend — defensiv auch clientseitig sortieren (A-122: einfache, feste Sortierung).
  const sorted = useMemo(
    () =>
      [...containers].sort((a, b) =>
        (completedAtOf(b) ?? '').localeCompare(completedAtOf(a) ?? ''),
      ),
    [containers],
  );

  if (containers.length === 0) {
    return (
      <div data-testid="archive-empty">
        <EmptyState title={t('archive.empty')} />
      </div>
    );
  }

  return (
    <Card data-testid="archive-table">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Archive className="h-5 w-5 text-muted-foreground" />
          {t('archive.tableTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <THead>
            <TR>
              <TH>{t('dashboard.col.number')}</TH>
              <TH>{t('dashboard.col.carrier')}</TH>
              <TH>{t('archive.col.completedAt')}</TH>
              <TH>{t('dashboard.col.status')}</TH>
              <TH className="text-right">{t('dashboard.col.action')}</TH>
            </TR>
          </THead>
          <TBody>
            {sorted.map((c) => (
              <TR
                key={c.trackedContainerId}
                data-testid={`archive-row-${c.trackedContainerId}`}
                className="cursor-pointer [&>td]:py-1.5"
                onClick={() => setDetail({ id: c.trackedContainerId, number: c.containerNumber })}
                title={t('premium.detail.open')}
              >
                <TD className="font-mono">
                  {c.containerNumber}
                  {c.sizeType && (
                    <span
                      className="ml-1.5 text-xs text-muted-foreground"
                      title={c.isoType ? `ISO ${c.isoType}` : undefined}
                    >
                      {c.sizeType}
                    </span>
                  )}
                </TD>
                <TD>
                  <span
                    className="block max-w-[150px] truncate"
                    title={c.carrierName ?? c.carrierCode ?? undefined}
                  >
                    {c.carrierName ?? c.carrierCode ?? '—'}
                  </span>
                </TD>
                <TD className="whitespace-nowrap">{fmtDate(completedAtOf(c))}</TD>
                <TD>
                  <CompletedBadge c={c} />
                </TD>
                <TD className="text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      title={t('premium.detail.open')}
                      aria-label={t('premium.detail.open')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetail({ id: c.trackedContainerId, number: c.containerNumber });
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {canManage(c) && (
                      <>
                        <Button
                          variant="secondary"
                          size="icon"
                          title={t('archive.reactivate')}
                          aria-label={t('archive.reactivate')}
                          disabled={busyId === c.trackedContainerId}
                          onClick={(e) => {
                            e.stopPropagation();
                            onReactivate(c);
                          }}
                        >
                          {busyId === c.trackedContainerId ? (
                            <Spinner />
                          ) : (
                            <RotateCcw className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t('dashboard.delete')}
                          aria-label={t('dashboard.delete')}
                          disabled={busyId === c.trackedContainerId}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRequestDelete(c);
                          }}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </CardContent>

      <ContainerDetailModal
        containerId={detail?.id ?? null}
        containerNumber={detail?.number ?? null}
        token={token}
        onClose={() => setDetail(null)}
        fmtDate={fmtDate}
      />
    </Card>
  );
}
